'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    cancelTeleconsultation,
    getTeleconsultations,
    resendTeleconsultationLink,
    TELECONSULTATION_STATUS_COLORS,
    TELECONSULTATION_STATUS_LABELS,
    TeleconsultationRoom,
    TeleconsultationStatus,
} from '@/lib/api/teleconsultation/teleconsultation-api'
import { PartnerSelector } from '@/components/partner/PartnerSelector'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { usePaginatedPage } from '@/lib/hooks/use-paginated-page'
import { useUrlPage } from '@/lib/hooks/use-url-page'
import PaginationControls from '@/components/pagination/PaginationControls'
import { useAuth } from '@/store/hooks'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useCallback, useMemo, useState } from 'react'
import {
    MdAdd,
    MdArrowForward,
    MdCancel,
    MdClose,
    MdContentCopy,
    MdPlayArrow,
    MdSearch,
    MdSend,
    MdVideocam,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

export default function TeleconsultationsPage() {
    const { user, isPartner, isEmployee } = useAuth()
    const hidePartnerSelector = isPartner || (isEmployee && !!user?.partnerId)
    const isIntermediation = (user?.company as any)?.secondarySegment === 'INTERMEDIATION'
    const navigate = useNavigate()
    const { canCreate } = useResourcePermissions('bookings', 'teleconsultations')

    const [showSearch, setShowSearch] = useState(false)
    const [searchInput, setSearchInput] = useState('')
    const [statusFilter, setStatusFilter] = useState<string>('all')
    const [filterById, setFilterById] = useState('')
    const [filterByCpf, setFilterByCpf] = useState('')
    const [showCancelModal, setShowCancelModal] = useState(false)
    const [cancellingId, setCancellingId] = useState<number | null>(null)
    const [cancelling, setCancelling] = useState(false)
    const [partnerFilter, setPartnerFilter] = useState<number | null>(null)

    const debouncedSearch = useDebounce(searchInput, 300)
    const debouncedId = useDebounce(filterById, 300)
    const debouncedCpf = useDebounce(filterByCpf, 300)

    const filters = useMemo(() => ({
        search: debouncedSearch || undefined,
        status: statusFilter !== 'all' ? statusFilter as TeleconsultationStatus : undefined,
        id: debouncedId ? Number(debouncedId) : undefined,
        cpf: debouncedCpf ? debouncedCpf.replace(/\D/g, '') : undefined,
        partnerId: partnerFilter || undefined,
    }), [debouncedSearch, statusFilter, debouncedId, debouncedCpf, partnerFilter])

    const fetchFn = useCallback((params: any) => {
        return getTeleconsultations(params)
    }, [])

    const [urlPage, setUrlPage] = useUrlPage()

    const { items, loading, page, limit, total, setPage, reload } = usePaginatedPage<TeleconsultationRoom, typeof filters>({
        fetchFn,
        filters,
        limit: 15,
        enabled: !!user?.companyId,
        page: urlPage,
        onPageChange: setUrlPage,
    })

    const handleCancel = async () => {
        if (!cancellingId) return

        setCancelling(true)
        try {
            const response = await cancelTeleconsultation(cancellingId)
            if (response.success) {
                toast.success('Teleconsulta cancelada com sucesso')
                reload()
            } else {
                toast.error(response.error || 'Erro ao cancelar teleconsulta')
            }
        } catch {
            toast.error('Erro ao cancelar teleconsulta')
        } finally {
            setCancelling(false)
            setShowCancelModal(false)
            setCancellingId(null)
        }
    }

    const handleResendLink = async (id: number) => {
        try {
            const response = await resendTeleconsultationLink(id)
            if (response.success) {
                toast.success('Link reenviado com sucesso')
            } else {
                toast.error(response.error || 'Erro ao reenviar link')
            }
        } catch {
            toast.error('Erro ao reenviar link')
        }
    }

    const handleCopyLink = (tc: TeleconsultationRoom) => {
        const link = tc.accessLink || `${window.location.origin}/t/${tc.patientToken}`
        navigator.clipboard.writeText(link)
        toast.success('Link copiado para a área de transferência')
    }

    const formatScheduledAt = (date: string) => {
        return format(new Date(date), "dd/MM/yyyy 'às' HH:mm", {
            locale: ptBR,
        })
    }

    const canStart = (status: TeleconsultationStatus) =>
        ['CREATED', 'WAITING_DOCTOR'].includes(status)

    const canCancelTc = (status: TeleconsultationStatus) =>
        ['CREATED', 'WAITING_PATIENT', 'WAITING_DOCTOR'].includes(status)

    if (loading && items.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando teleconsultas...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg">
                        <MdVideocam className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            Teleconsultas
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {total}{' '}
                            {total === 1
                                ? 'teleconsulta'
                                : 'teleconsultas'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {showSearch ? (
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                placeholder="Buscar teleconsultas..."
                                value={searchInput}
                                onChange={e => setSearchInput(e.target.value)}
                                className="input input-bordered input-sm w-64"
                                autoFocus
                            />
                            <button
                                onClick={() => {
                                    setShowSearch(false)
                                    setSearchInput('')
                                }}
                                className="btn btn-sm btn-ghost"
                            >
                                <MdClose />
                            </button>
                        </div>
                    ) : (
                        <>
                            <button
                                onClick={() => setShowSearch(true)}
                                className="btn btn-sm btn-ghost gap-2"
                            >
                                <MdSearch />
                            </button>
                            {canCreate && (
                                <button
                                    onClick={() =>
                                        navigate('/atendimento/scheduling?type=TELECONSULTATION')
                                    }
                                    className="btn btn-secondary gap-2"
                                >
                                    <MdAdd />
                                    Nova Teleconsulta
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Filtros */}
            <div className="flex gap-2 flex-wrap">
                {[
                    { value: 'all', label: 'Todas' },
                    { value: 'CREATED', label: 'Criadas' },
                    { value: 'WAITING_PATIENT', label: 'Ag. Paciente' },
                    { value: 'IN_PROGRESS', label: 'Em Andamento' },
                    { value: 'COMPLETED', label: 'Concluídas' },
                    { value: 'CANCELLED', label: 'Canceladas' },
                ].map(filter => (
                    <button
                        key={filter.value}
                        onClick={() => setStatusFilter(filter.value)}
                        className={`btn btn-sm ${statusFilter === filter.value
                            ? 'btn-secondary'
                            : 'btn-ghost'
                            }`}
                    >
                        {filter.label}
                    </button>
                ))}
            </div>

            {/* Filtros por ID e CPF */}
            <div className="flex gap-3 flex-wrap items-end">
                <div className="form-control">
                    <label className="label py-0">
                        <span className="label-text text-xs font-medium">Numero</span>
                    </label>
                    <input
                        type="number"
                        placeholder="0"
                        value={filterById}
                        onChange={e => setFilterById(e.target.value)}
                        className="input input-bordered input-sm w-24"
                        min={1}
                    />
                </div>
                <div className="form-control">
                    <label className="label py-0">
                        <span className="label-text text-xs font-medium">CPF do Paciente</span>
                    </label>
                    <input
                        type="text"
                        placeholder="000.000.000-00"
                        value={filterByCpf}
                        onChange={e => {
                            const nums = e.target.value.replace(/\D/g, '')
                            let m = nums
                            if (nums.length > 3) m = nums.slice(0, 3) + '.' + nums.slice(3)
                            if (nums.length > 6) m = m.slice(0, 7) + '.' + nums.slice(6)
                            if (nums.length > 9) m = m.slice(0, 11) + '-' + nums.slice(9, 11)
                            setFilterByCpf(m)
                        }}
                        className="input input-bordered input-sm w-44"
                        maxLength={14}
                    />
                </div>
                {!hidePartnerSelector && (
                    <PartnerSelector
                        value={partnerFilter}
                        onChange={(id) => setPartnerFilter(id)}
                        label="Parceiro"
                        placeholder="Todos"
                        className="!w-48"
                    />
                )}
            </div>

            {/* Empty state */}
            {items.length === 0 && !loading ? (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-12 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-violet-100 flex items-center justify-center">
                        <MdVideocam className="text-3xl text-violet-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-base-content mb-2">
                        Nenhuma teleconsulta encontrada
                    </h3>
                    <p className="text-sm text-base-content/60 mb-4">
                        Crie sua primeira teleconsulta para atender pacientes
                        remotamente.
                    </p>
                    {canCreate && (
                        <button
                            onClick={() => navigate('/atendimento/scheduling?type=TELECONSULTATION')}
                            className="btn btn-secondary gap-2"
                        >
                            <MdAdd />
                            Nova Teleconsulta
                        </button>
                    )}
                </div>
            ) : (
                <>
                    {/* Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {items.map(tc => (
                            <div
                                key={tc.id}
                                className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-5 hover:shadow-md transition-shadow"
                            >
                                <div className="flex items-start justify-between mb-3">
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-semibold text-base-content truncate">
                                            {tc.client?.name || 'Paciente'}
                                        </h3>
                                        <p className="text-sm text-base-content/60 truncate">
                                            {tc.professional?.name ||
                                                'Profissional'}
                                        </p>
                                    </div>
                                    <span
                                        className={`badge badge-sm ${TELECONSULTATION_STATUS_COLORS[
                                            tc.status
                                        ]
                                            }`}
                                    >
                                        {
                                            TELECONSULTATION_STATUS_LABELS[
                                            tc.status
                                            ]
                                        }
                                    </span>
                                </div>

                                {tc.title && (
                                    <p className="text-sm text-base-content/70 mb-2 truncate">
                                        {tc.title}
                                    </p>
                                )}

                                <div className="text-sm text-base-content/60 mb-2">
                                    <p>{formatScheduledAt(tc.scheduledAt)}</p>
                                    <p>
                                        Duração:{' '}
                                        {Math.round(tc.maxDuration / 60)} min
                                    </p>
                                </div>

                                {(tc.price != null || tc.sellingPrice != null || tc.consultation?.price != null) && (
                                    <div className="flex flex-wrap items-center gap-2 text-xs text-base-content/60 mb-4">
                                        {(tc.price ?? tc.consultation?.price) != null && (tc.price ?? tc.consultation?.price)! > 0 && (
                                            <span>Custo: R$ {((tc.price ?? tc.consultation?.price)! / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                        )}
                                        {(tc.sellingPrice ?? tc.consultation?.sellingPrice) != null && (tc.sellingPrice ?? tc.consultation?.sellingPrice)! > 0 && (
                                            <span className="font-semibold text-secondary">
                                                Venda: R$ {((tc.sellingPrice ?? tc.consultation?.sellingPrice)! / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </span>
                                        )}
                                        {(tc.markupPercentage ?? tc.consultation?.markupPercentage) != null && (tc.markupPercentage ?? tc.consultation?.markupPercentage)! > 0 && (
                                            <span>{(tc.markupPercentage ?? tc.consultation?.markupPercentage)!.toFixed(1)}%</span>
                                        )}
                                    </div>
                                )}

                                <div className="flex items-center gap-1 flex-wrap">
                                    <button
                                        onClick={() =>
                                            navigate(`/bookings/teleconsultations/${tc.id}`)
                                        }
                                        className="btn btn-ghost btn-xs gap-1"
                                    >
                                        Detalhes
                                        <MdArrowForward />
                                    </button>

                                    {!isIntermediation && canStart(tc.status) && (
                                        <button
                                            onClick={() =>
                                                navigate(
                                                    `/bookings/teleconsultations/${tc.id}/room`
                                                )
                                            }
                                            className="btn btn-secondary btn-xs gap-1"
                                        >
                                            <MdPlayArrow />
                                            Iniciar
                                        </button>
                                    )}

                                    {!isIntermediation && ['WAITING_PATIENT', 'IN_PROGRESS'].includes(tc.status) && (
                                        <button
                                            onClick={() =>
                                                navigate(
                                                    `/bookings/teleconsultations/${tc.id}/room`
                                                )
                                            }
                                            className="btn btn-secondary btn-xs gap-1"
                                        >
                                            <MdVideocam />
                                            Entrar
                                        </button>
                                    )}

                                    <button
                                        onClick={() => handleCopyLink(tc)}
                                        className="btn btn-ghost btn-xs gap-1"
                                        title="Copiar link do paciente"
                                    >
                                        <MdContentCopy />
                                    </button>

                                    {canCancelTc(tc.status) && (
                                        <>
                                            <button
                                                onClick={() =>
                                                    handleResendLink(tc.id)
                                                }
                                                className="btn btn-ghost btn-xs gap-1"
                                                title="Reenviar link"
                                            >
                                                <MdSend />
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setCancellingId(tc.id)
                                                    setShowCancelModal(true)
                                                }}
                                                className="btn btn-ghost btn-xs text-error gap-1"
                                                title="Cancelar"
                                            >
                                                <MdCancel />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {total > limit && (
                        <PaginationControls
                            page={page}
                            limit={limit}
                            total={total}
                            loading={loading}
                            onPageChange={setPage}
                            label="teleconsulta"
                        />
                    )}
                </>
            )}
            <ConfirmModal
                isOpen={showCancelModal}
                onClose={() => {
                    setShowCancelModal(false)
                    setCancellingId(null)
                }}
                onConfirm={handleCancel}
                title="Cancelar teleconsulta"
                message="Deseja realmente cancelar esta teleconsulta? Esta ação não pode ser desfeita."
                loading={cancelling}
                confirmButtonText="Cancelar"
            />
        </div>
    )
}
